import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  Divider,
  CircularProgress,
  Button,
  Stack,
} from '@mui/material';

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:7071')
  .replace(/\/api$/, '')
  .replace(/\/$/, '');

export default function ExplorePreview({ postId, hideBack = false }) {
  const params = useParams();
  const effectiveId = postId || params?.id;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [post, setPost] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchPost = async () => {
      if (!effectiveId) {
        setPost(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_ORIGIN}/api/explore/${effectiveId}`);
        if (!res.ok) {
          throw new Error(`Request failed (${res.status})`);
        }
        const data = await res.json();
        if (isMounted) {
          setPost(data?.data || null);
        }
      } catch (err) {
        if (isMounted) setError(err?.message || 'Failed to load preview');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchPost();
    return () => { isMounted = false; };
  }, [effectiveId]);

  const formattedDate = useMemo(() => {
    if (!post?.updatedAt) return '';
    try {
      return new Date(post.updatedAt).toLocaleString();
    } catch {
      return '';
    }
  }, [post?.updatedAt]);

  const heroImage = post?.imageUrl;

  return (
    <Box sx={{ maxWidth: 880, mx: 'auto', px: { xs: 2, md: 4 }, py: 4 }}>
      {!hideBack && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Previewing Explore post
          </Typography>
          <Button variant="outlined" size="small" onClick={() => navigate(-1)}>
            Back
          </Button>
        </Stack>
      )}

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
        </Box>
      )}

      {!loading && !error && post && (
        <Box>
          {heroImage ? (
            <Box
              component="img"
              src={heroImage}
              alt={post.title}
              sx={{
                width: '100%',
                maxHeight: 420,
                objectFit: 'cover',
                borderRadius: 3,
                mb: 3,
                boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
              }}
            />
          ) : null}

          {post.heroEyebrow ? (
            <Typography variant="overline" color="primary" sx={{ letterSpacing: 1.2 }}>
              {post.heroEyebrow}
            </Typography>
          ) : null}

          <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>
            {post.title}
          </Typography>

          <Stack direction="row" spacing={1.5} sx={{ mt: 2, mb: 3 }} alignItems="center">
            {post.readMinutes ? (
              <Typography variant="body2" color="text.secondary">
                {post.readMinutes} min read
              </Typography>
            ) : null}
            {formattedDate ? (
              <>
                <Divider orientation="vertical" flexItem />
                <Typography variant="body2" color="text.secondary">
                  Updated {formattedDate}
                </Typography>
              </>
            ) : null}
            {post.status ? (
              <>
                <Divider orientation="vertical" flexItem />
                <Chip
                  label={post.status}
                  size="small"
                  color={post.status === 'published' ? 'success' : post.status === 'archived' ? 'default' : 'warning'}
                  variant="outlined"
                />
              </>
            ) : null}
          </Stack>

          {post.summary ? (
            <Typography variant="subtitle1" sx={{ mb: 4, color: 'text.secondary', lineHeight: 1.6 }}>
              {post.summary}
            </Typography>
          ) : null}

          <Divider sx={{ my: 4 }} />

          <Box
            sx={{
              '& h1': { fontSize: '2rem', fontWeight: 700, mt: 4, mb: 2 },
              '& h2': { fontSize: '1.75rem', fontWeight: 700, mt: 4, mb: 2 },
              '& h3': { fontSize: '1.5rem', fontWeight: 700, mt: 3, mb: 1.5 },
              '& p': { mt: 0, mb: 2, lineHeight: 1.8, color: 'text.primary' },
              '& ul, & ol': { pl: 3, mb: 3, color: 'text.primary' },
              '& li': { mb: 1 },
              '& img': { maxWidth: '100%', borderRadius: 2, my: 3 },
              '& blockquote': {
                borderLeft: '4px solid rgba(200,168,107,0.4)',
                paddingLeft: 2,
                fontStyle: 'italic',
                color: 'text.secondary',
                my: 3,
              },
            }}
            dangerouslySetInnerHTML={{ __html: post.content || '' }}
          />

          {post.tags?.length ? (
            <>
              <Divider sx={{ my: 4 }} />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {post.tags.map((tag) => (
                  <Chip key={tag} label={tag} variant="outlined" />
                ))}
              </Stack>
            </>
          ) : null}
        </Box>
      )}
    </Box>
  );
}
